import { useEffect, useState } from 'react';
import { useApp } from '../app/store.ts';
import { archive } from '../archive/store.ts';
import { ensureAttachment, listAttachments } from '../data/attachments.ts';
import { onDocsChanged } from '../data/docs.ts';
import { deriveTitle } from '../data/markdown.ts';
import { Editor } from '../editor/Editor.tsx';
import { DocumentStrip } from './DocumentStrip.tsx';
import { Icon } from './Icon.tsx';
import type { ArchiveEntry } from '../archive/store.ts';
import type { AttachmentMeta } from '../data/attachments.ts';

/**
 * Ein geöffnetes Dokument (docs/KONZEPT.md, Abschnitt 19.6).
 *
 * Bis hierher öffnete ein Dokument den Notiz-Editor mit einem Streifen darüber — es
 * sah aus wie eine Notiz, die zufällig eine Datei dabei hat. Das ist verkehrt herum:
 * Im Mittelpunkt steht das Blatt Papier, die eigene Notiz ist das Beiwerk.
 */

export function DocumentView({ note }: { note: ArchiveEntry }): JSX.Element {
  const { open, trash } = useApp();
  const [file, setFile] = useState<{ id: string; meta: AttachmentMeta } | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [notiz, setNotiz] = useState(false);

  useEffect(() => {
    let alive = true;
    const nachsehen = (): void => {
      void listAttachments(note.id).then((all) => {
        const first = Object.entries(all)[0];
        if (!alive || !first) return;
        setFile({ id: first[0], meta: first[1] });
      });
    };
    nachsehen();
    const stop = onDocsChanged((id) => {
      if (id === note.id) nachsehen();
    });
    return () => {
      alive = false;
      stop();
    };
  }, [note.id]);

  useEffect(() => {
    if (!file) return;
    void ensureAttachment(note.id, file.id).then(setUrl);
  }, [note.id, file]);

  const mime = file?.meta.mime ?? '';
  const istBild = mime.startsWith('image/');
  const istPdf = mime === 'application/pdf' || file?.meta.name.toLowerCase().endsWith('.pdf') === true;

  return (
    <section className="document">
      <header className="note-head">
        <button className="head-icon back" onClick={() => open(null)} title="Zurück">
          <Icon name="back" />
        </button>
        <span className="document-title">{deriveTitle(note.content)}</span>
        <span className="spacer" />
        <button
          className="head-icon"
          title="In den Papierkorb"
          onClick={() => void archive.remove(note.id).then(() => open(null))}
        >
          <Icon name="trash" />
        </button>
      </header>

      <div className="document-body">
        {/* Das Blatt zuerst — deshalb ist man hier. */}
        <div className="document-sheet">
          {url && istBild && <img src={url} alt={file?.meta.name ?? ''} />}
          {url && istPdf && (
            <object data={url} type="application/pdf" aria-label={file?.meta.name ?? 'Dokument'}>
              <p className="hint">
                Dieser Browser zeigt keine PDFs im Fenster.{' '}
                <a href={url} target="_blank" rel="noreferrer">
                  In neuem Tab öffnen
                </a>
              </p>
            </object>
          )}
          {url && !istBild && !istPdf && (
            <div className="document-datei">
              <p>{file?.meta.name}</p>
              <a className="ghost" href={url} download={file?.meta.name}>
                Datei öffnen
              </a>
            </div>
          )}
          {!url && file && <p className="hint">Datei wird geladen …</p>}
        </div>

        <aside className="document-side">
          <DocumentStrip note={note} />

          {/* Die eigene Notiz ist Beiwerk und bleibt eingeklappt, bis sie gebraucht wird. */}
          <button className="doc-file" onClick={() => setNotiz(!notiz)}>
            {notiz ? 'Notiz ausblenden' : 'Eigene Notiz und Schlagwörter'}
          </button>
          {notiz && (
            <div className="document-notiz">
              <Editor noteId={note.id} />
            </div>
          )}

          <button className="doc-file" onClick={() => void trash(note.id)}>
            Dokument in den Papierkorb
          </button>
        </aside>
      </div>
    </section>
  );
}
